import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, ShieldAlert, Lightbulb, AlertTriangle, Star, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

type KeyPoint = {
  id: string; title: string; content: string; expandableDetail: string;
  category: string; sopLink: string; sortOrder: number;
};

const CATEGORY_STYLES: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  Safety: { icon: ShieldAlert, color: 'text-destructive', bg: 'bg-destructive/10 border-destructive/30' },
  Warning: { icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700' },
  Tip: { icon: Lightbulb, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700' },
  'Key Fact': { icon: Star, color: 'text-primary', bg: 'bg-primary/5 border-primary/20' },
  Escalation: { icon: ArrowUpRight, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700' },
  Standard: { icon: Lightbulb, color: 'text-foreground', bg: 'bg-muted border-border' },
};

function KeyPointCard({ kp }: { kp: KeyPoint }) {
  const [expanded, setExpanded] = useState(false);
  const style = CATEGORY_STYLES[kp.category] || CATEGORY_STYLES.Standard;
  const Icon = style.icon;

  return (
    <div className={`border rounded-xl p-4 ${style.bg}`}>
      <button onClick={() => kp.expandableDetail && setExpanded(!expanded)}
        className="w-full text-left flex items-start gap-3">
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${style.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-foreground">{kp.title}</h3>
            {kp.expandableDetail && (
              expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
          </div>
          <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{kp.content}</p>
        </div>
      </button>
      {expanded && kp.expandableDetail && (
        <div className="mt-3 ml-8 pl-3 border-l-2 border-current/10">
          <p className="text-sm text-foreground/70 leading-relaxed whitespace-pre-wrap">{kp.expandableDetail}</p>
          {kp.sopLink && (
            <a href={kp.sopLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary mt-2 hover:underline">
              <ExternalLink className="w-3 h-3" /> View SOP
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function LearnStep({ keyPoints, commonMistakes, referenceMaterials, onContinue }: {
  keyPoints: KeyPoint[];
  commonMistakes: string;
  referenceMaterials: string;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Key Learning Points</h2>
      <p className="text-sm text-muted-foreground">Tap each card to expand and learn more. Review all points before continuing.</p>

      <div className="space-y-3">
        {keyPoints.map(kp => <KeyPointCard key={kp.id} kp={kp} />)}
      </div>

      {commonMistakes && (
        <div className="bg-muted rounded-xl p-4">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">⚠ Common Mistakes to Avoid</p>
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{commonMistakes}</p>
        </div>
      )}

      {referenceMaterials && (
        <div className="border border-border rounded-xl p-4">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">📄 Reference Materials</p>
          <p className="text-sm text-foreground whitespace-pre-wrap">{referenceMaterials}</p>
        </div>
      )}

      <Button onClick={onContinue} className="w-full h-12 text-base">
        I've Reviewed the Key Points
      </Button>
    </div>
  );
}
